/**
 * Example configurations that pass `validateLotteryConfig` — used by contracts/core tests, the seed and the
 * publishing simulator as starting points. Demo currency is XTS (D-002). Ids are fixed ULIDs so test vectors
 * stay stable.
 */
import { LotteryConfigSchema } from './lottery-config.js';
export const EXAMPLE_IDS = {
    prizeGrand: '01JCPR1ZE0000000000GRAND00',
    prizeMajor: '01JCPR1ZE0000000000MAJ0R00',
    prizeMinor: '01JCPR1ZE0000000000M1N0R00',
    prizeSample: '01JCPR1ZE000000000SAMP1E00',
    sponsorAurora: '01JCSP0NS0R00000000AVR0RA0',
    samplingRule: '01JCSAMP11NGRV1E0000000000',
    taskReferral: '01JCTASK000000000REFERRA10',
    taskFollow: '01JCTASK0000000000F0110W00',
};
const theme = {
    canvas: '#0b0f1a',
    surface: '#141a2a',
    textPrimary: '#f5f7fb',
    textSecondary: '#b8c0d4',
    border: '#2a3350',
    action: '#ffb000',
    actionText: '#1a1200',
    focus: '#7cc4ff',
    success: '#7ee2a8',
    warning: '#ffd166',
    error: '#ff8a8a',
    sponsorAccent: '#8ab4ff',
    prizeAccent: '#ff7a59',
};
const owner = {
    ownerId: 'client-logistics',
    name: 'Client logistics',
    dispatchSlaBusinessDays: 3,
    escalationRoute: 'ops@windrop.test',
};
/** Scheduled raffle, free entry, one sponsor with a first-500 sampling rule. */
export const exampleScheduledRaffleConfig = (overrides = {}) => LotteryConfigSchema.parse({
    configVersion: 1,
    identity: {
        slug: 'aurora-launch-raffle',
        marketPolicyRef: 'market-xts-default',
        locales: ['en'],
        currency: 'XTS',
        timezone: 'Africa/Lagos',
        termsVersion: '2026-11',
        privacyVersion: '2026-11',
        supportRoute: '/support',
    },
    entry: {
        mode: 'FREE',
        priceMinor: '0',
        requiredContact: 'EITHER',
        eligibleQuantity: { min: 1, max: 1 },
        caps: { perPerson: 1, global: 100000 },
        bonusEntryCap: 10,
    },
    eligibility: { minAge: 18 },
    schedule: {
        opensAt: '2026-11-10T09:00:00.000Z',
        closesAt: '2026-11-20T18:00:00.000Z',
        drawAt: '2026-11-21T18:00:00.000Z',
    },
    outcome: {
        mechanic: 'SCHEDULED_RAFFLE',
        scheduledRaffle: {
            tiers: [
                { tier: 'GRAND', prizeId: EXAMPLE_IDS.prizeGrand, winners: 1 },
                { tier: 'MAJOR', prizeId: EXAMPLE_IDS.prizeMajor, winners: 2 },
                { tier: 'MINOR', prizeId: EXAMPLE_IDS.prizeMinor, winners: 5 },
            ],
        },
        oddsDisclosure: {
            basis: 'DEPENDS_ON_ENTRIES',
            statement: { en: 'Eight prizes are drawn from every eligible entry.' },
        },
    },
    prizes: {
        prizes: [
            {
                prizeId: EXAMPLE_IDS.prizeGrand,
                name: { en: 'City car' },
                tier: 'GRAND',
                quantity: 1,
                value: { amountMinor: '1200000000', currency: 'XTS' },
                fulfillmentType: 'IN_PERSON',
                fulfillmentOwner: owner,
                selectionMode: 'FIXED_PRODUCT',
            },
            {
                prizeId: EXAMPLE_IDS.prizeMajor,
                name: { en: 'Flagship phone' },
                tier: 'MAJOR',
                quantity: 2,
                value: { amountMinor: '95000000', currency: 'XTS' },
                fulfillmentType: 'PHYSICAL',
                fulfillmentOwner: owner,
                selectionMode: 'VALUE_CAP',
                approvedBrandIds: ['01JCBRAND00000000000000001'],
                valueCapMinor: '95000000',
            },
            {
                prizeId: EXAMPLE_IDS.prizeMinor,
                name: { en: 'Airtime voucher' },
                tier: 'MINOR',
                quantity: 5,
                value: { amountMinor: '500000', currency: 'XTS' },
                fulfillmentType: 'DIGITAL',
                fulfillmentOwner: owner,
            },
            {
                prizeId: EXAMPLE_IDS.prizeSample,
                name: { en: 'Aurora Buds' },
                tier: 'SPONSOR_SAMPLE',
                quantity: 500,
                value: { amountMinor: '2500000', currency: 'XTS' },
                fulfillmentType: 'PHYSICAL',
                fulfillmentOwner: owner,
                sponsorId: EXAMPLE_IDS.sponsorAurora,
            },
        ],
        prizeLiabilityCapMinor: '3000000000',
    },
    sponsors: {
        sponsors: [
            {
                sponsorId: EXAMPLE_IDS.sponsorAurora,
                name: 'Aurora Audio',
                tier: 'TITLE',
                displayWeight: 10,
                placements: ['HERO_LOCKUP', 'SPONSOR_STRIP', 'WIN_CREDIT', 'SAMPLE_REVEAL'],
                brandColor: '#8ab4ff',
                sponsoredPrizeIds: [EXAMPLE_IDS.prizeSample],
            },
        ],
    },
    sampling: {
        rules: [
            {
                ruleId: EXAMPLE_IDS.samplingRule,
                sponsorId: EXAMPLE_IDS.sponsorAurora,
                defaultPrizeId: EXAMPLE_IDS.prizeSample,
                eligible: { firstN: 500 },
                reservedUnits: 500,
                priority: 1,
                copy: {
                    reveal: {
                        en: "You didn't win the {mainPrize} this time — but you've won {product} from {sponsor} as part of the published sponsor sample.",
                    },
                },
            },
        ],
    },
    engagement: {
        platforms: [
            { platform: 'WHATSAPP', accountUrl: 'https://wa.me/2340000000000', displayOrder: 0 },
            { platform: 'INSTAGRAM', accountUrl: 'https://instagram.com/windrop.demo', displayOrder: 1 },
        ],
        tasks: [
            {
                taskId: EXAMPLE_IDS.taskReferral,
                type: 'REFERRAL',
                title: { en: 'Invite a friend' },
                consentText: { en: 'Your friend will see your display name.' },
            },
            {
                taskId: EXAMPLE_IDS.taskFollow,
                type: 'FOLLOW',
                platform: 'INSTAGRAM',
                destinationUrl: 'https://instagram.com/windrop.demo',
                title: { en: 'Follow us on Instagram' },
                consentText: { en: 'We record that you attested to following.' },
            },
        ],
        totalBonusCap: 10,
    },
    presentation: {
        theme,
        heroMedia: {
            kind: 'IMAGE',
            ref: 'media/hero-car.avif',
            altText: 'A silver city car parked under studio lights',
        },
        revealStyle: 'DRAW_ROOM',
        energyLevel: 'FULL',
        soundDefault: false,
        sponsorPlacements: ['HERO_LOCKUP', 'SPONSOR_STRIP'],
        content: {
            en: {
                title: 'Aurora launch raffle',
                prizeStatement: 'Win a city car, two flagship phones or airtime.',
            },
        },
    },
    claims: {
        claimWindowDays: 14,
        variantHoldMinutes: 15,
        holdAutoExtensions: 1,
        unclaimedPrizePolicy: 'REDRAW',
    },
    ...overrides,
});
/** Fixed-pool instant win (1,000 tickets, 60 winning), paid entry. */
export const exampleInstantWinConfig = (overrides = {}) => {
    const base = exampleScheduledRaffleConfig();
    return LotteryConfigSchema.parse({
        ...base,
        identity: { ...base.identity, slug: 'aurora-instant-win' },
        entry: { ...base.entry, mode: 'PAID', priceMinor: '50000', caps: { perPerson: 10, global: 1000 } },
        schedule: { opensAt: base.schedule.opensAt, closesAt: base.schedule.closesAt },
        outcome: {
            mechanic: 'INSTANT_WIN',
            fixedPool: {
                poolSize: 1000,
                outcomes: [
                    { prizeId: EXAMPLE_IDS.prizeMajor, count: 2 },
                    { prizeId: EXAMPLE_IDS.prizeMinor, count: 58 },
                ],
            },
            oddsDisclosure: {
                basis: 'W_OF_N',
                winningTickets: 60,
                poolSize: 1000,
                statement: { en: '60 winning tickets in a pool of 1,000.' },
            },
        },
        prizes: {
            ...base.prizes,
            prizes: base.prizes.prizes.map((p) => p.prizeId === EXAMPLE_IDS.prizeMinor ? { ...p, quantity: 58 } : p),
        },
        presentation: { ...base.presentation, revealStyle: 'INSTANT_CARD' },
        ...overrides,
    });
};
/** Number pick 5 of 1–49 with three match tiers, paid entry. */
export const exampleNumberPickConfig = (overrides = {}) => {
    const base = exampleScheduledRaffleConfig();
    return LotteryConfigSchema.parse({
        ...base,
        identity: { ...base.identity, slug: 'aurora-number-pick' },
        entry: {
            ...base.entry,
            mode: 'PAID',
            priceMinor: '20000',
            eligibleQuantity: { min: 1, max: 5 },
            caps: { perPerson: 20 },
        },
        outcome: {
            mechanic: 'NUMBER_PICK',
            numberPick: {
                range: { min: 1, max: 49 },
                pickCount: 5,
                matchTiers: [
                    { matches: 5, prizeId: EXAMPLE_IDS.prizeGrand, prizeUnits: 1 },
                    { matches: 4, prizeId: EXAMPLE_IDS.prizeMajor, prizeUnits: 2 },
                    { matches: 3, prizeId: EXAMPLE_IDS.prizeMinor, prizeUnits: 5 },
                ],
                quickPick: 'ALLOWED',
                maxLiabilityMinor: '1392500000',
            },
            oddsDisclosure: {
                basis: 'ONE_IN_N',
                statement: { en: 'Match 3, 4 or 5 of the 5 drawn numbers from 1–49.' },
            },
        },
        prizes: { ...base.prizes, prizeLiabilityCapMinor: '5000000000' },
        presentation: { ...base.presentation, revealStyle: 'NUMBER_BOARD' },
        ...overrides,
    });
};
/** First-N: every 100th unique participant wins airtime, up to 5 winners. */
export const exampleFirstNConfig = (overrides = {}) => {
    const base = exampleScheduledRaffleConfig();
    return LotteryConfigSchema.parse({
        ...base,
        identity: { ...base.identity, slug: 'aurora-first-n' },
        schedule: { opensAt: base.schedule.opensAt, closesAt: base.schedule.closesAt },
        outcome: {
            mechanic: 'FIRST_N',
            firstN: {
                countingMode: 'UNIQUE_PARTICIPANT',
                everyNth: { n: 100, prizeId: EXAMPLE_IDS.prizeMinor, limit: 5 },
                milestones: [{ position: 1, prizeId: EXAMPLE_IDS.prizeMajor }],
            },
            oddsDisclosure: {
                basis: 'FIXED_POSITIONS',
                statement: { en: 'The first participant and every 100th participant up to 500 win.' },
            },
        },
        presentation: { ...base.presentation, revealStyle: 'MILESTONE_COUNTER' },
        ...overrides,
    });
};
//# sourceMappingURL=examples.js.map